import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { AgreementMetrics } from '../agreements/api';
import type { SupportBankRow, UnbankedSale } from '../support/api';

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/clients responses)
// ---------------------------------------------------------------------------

export type ClientStatusFilter = 'active' | 'archived' | 'all';

export interface ClientListRow {
  id: string;
  name: string;
  notes: string | null;
  isActive: boolean;
  /** Accounts organization this client is linked to (Wave C2), or null. */
  hubOrgId: string | null;
  activeAgreements: number;
  /** Every agreement on record, archived included. */
  totalAgreements: number;
  ytdRevenue: number;
  openAr: number;
  overdueAr: number;
  /**
   * Delivery economics across ALL of this client's agreements (archived
   * included), rolled up by the agreements metrics engine. Same numbers, same
   * definitions, same arithmetic as the /agreements footer — so the two lists are
   * one instrument rather than two opinions.
   */
  hours: number;
  revenue: number;
  cost: number;
  margin: number | null;
  internalCost: number;
  /**
   * Everything delivered and never invoiced — money nobody has been asked for.
   * Billable time on HOURLY agreements **net of the prepaid support bank**, plus
   * uninvoiced billable expenses.
   *
   * ⚠ It is not the agreements engine's raw `unbilledAmount`. That figure is the
   * TIME half only: on a SUPPORT agreement it values hours the client bought in
   * advance (an invitation to charge twice for one hour), and it drops the
   * expenses the invoice wizard pulls onto the same draft. The server nets the
   * bank's own consumption ledger and adds the expenses before serializing —
   * `rollupMetrics().unbilled`, the one definition — so this column, the
   * footer, the subtitle, the CSV and /invoices/new all print one number.
   */
  unbilled: number;
  /** The expense half of `unbilled`, so a tooltip can name it. */
  unbilledExpenses: number;
  /** Uninvoiced support work a prepaid block already covered — the amount that
   *  came OUT of `unbilled`, printed so it is a fact and not a silence. */
  unbilledPrepaid: number;
  unbilledPrepaidHours: number;
  /**
   * Oldest still-unbilled billable entry ('YYYY-MM-DD'), or null. It is the
   * `from` the invoice wizard needs: a deep link carrying only a client id
   * lands on the picker, so without this the row's unbilled figure could name
   * money but not open it.
   */
  unbilledFrom: string | null;
  /**
   * The prepaid support bank, or null for a client who never bought hours.
   * `runsOut` is the CLAMPED projection from `HourBankService` — the earlier of
   * the pace draining the bank and the hours expiring — so the list, the client
   * page and /support cannot disagree about the date.
   */
  support: {
    balance: number;
    status: 'good' | 'ok' | 'low' | 'exhausted' | 'overage';
    overage: number;
    runsOut: string | null;
    runsOutInDays: number | null;
    runsOutReason: 'burn' | 'expiry' | null;
    /** What an hour of their support is worth — the bank's own rate, so the
     *  list can print the balance in money. Null when nothing has priced it. */
    rate: number | null;
  } | null;
}

/** One bucket of a portfolio rollup (mirrors MetricsRollup on the server). */
export interface MetricsRollup {
  agreements: number;
  hours: number;
  revenue: number;
  cost: number;
  margin: number | null;
}

export interface PortfolioRollup {
  client: MetricsRollup;
  internal: MetricsRollup;
  all: MetricsRollup;
  /** NET of the prepaid support bank and INCLUSIVE of uninvoiced billable
   *  expenses — see ClientListRow#unbilled. It is the only "unbilled" in this
   *  payload on purpose. */
  unbilled: number;
  /** The expense half of `unbilled`. */
  unbilledExpenses: number;
}

export interface ClientAgreement {
  id: string;
  name: string;
  code: string | null;
  type: 'PROJECT' | 'SUPPORT' | 'RECURRING' | 'INTERNAL';
  billBy: 'HOURS' | 'FIXED' | 'NONE';
  isActive: boolean;
  budgetHours: number | null;
  budgetAmount: number | null;
  hourlyRate: number | null;
  startDate: string | null;
  endDate: string | null;
  /** Linked hub project, read from OUR column — see AgreementsTable's HubBadge. */
  hubProjectIds: string[];
  /**
   * The SAME object /api/agreements returns for this agreement — produced by the
   * agreements module's metrics engine. Never recompute burn/revenue/margin here.
   */
  metrics: AgreementMetrics;
}

export interface ClientInvoice {
  id: string;
  number: string;
  issueDate: string;
  dueDate: string;
  state: 'DRAFT' | 'OPEN' | 'PAID' | 'PARTIAL' | 'VOID';
  isLate: boolean;
  kind: string;
  amount: number;
  dueAmount: number;
}

export interface SupportMonth {
  month: string;
  used: number;
  /** Hours × the client's support rate — null when no rate is known. */
  value: number | null;
  allowance: number | null;
  remaining: number | null;
  overage: number | null;
  pct: number | null;
}

/**
 * The client's support standing, **bank first**.
 *
 * `bank` is the identical row `/api/support/banks` ships — same balance, same
 * burn, same projected exhaustion, computed once by `HourBankService`. The
 * monthly allowance is kept as the exception it is (a genuine entitlement) and
 * is never rendered as a second verdict beside the bank's.
 */
export interface ClientSupport {
  bank: SupportBankRow | null;
  /** What an hour of their support is worth — last purchase rate, else the
   *  support agreement's rate. Null when we genuinely do not know. */
  rate: number | null;
  /** Hours sold on an invoice that never reached the bank. */
  unbankedSales: UnbankedSale[];
  allowance: { monthlyHours: number; effectiveFrom: string; effectiveTo: string | null } | null;
  supportAgreements: number;
  currentMonth: SupportMonth | null;
  history: SupportMonth[];
}

/** Bill-to block printed on this client's invoices. */
export interface ClientBilling {
  billingContactName: string | null;
  billingEmail: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  taxId: string | null;
}

export const EMPTY_BILLING: ClientBilling = {
  billingContactName: null,
  billingEmail: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  region: null,
  postalCode: null,
  country: null,
  taxId: null,
};

export interface ClientDetail {
  id: string;
  name: string;
  notes: string | null;
  isActive: boolean;
  /** Accounts organization this client is linked to (Wave C2), or null. */
  hubOrgId: string | null;
  createdAt: string;
  billing: ClientBilling;
  stats: {
    lifetimeRevenue: number;
    ytdRevenue: number;
    openAr: number;
    overdueAr: number;
    /** How many OPEN/PARTIAL invoices are past their due date. */
    overdueCount: number;
    /** Days past due on the oldest overdue invoice, null when nothing is late. */
    oldestOverdueDays: number | null;
    avgDaysToPay: number | null;
    activeAgreements: number;
    /** Every agreement on record — archived included. */
    totalAgreements: number;
    /** ALL invoices for this client. `invoices` below is only the first page. */
    invoiceCount: number;
    invoicesShown: number;
    lastInvoiceDate: string | null;
    /** Oldest still-unbilled billable entry — the invoice wizard's `from`. */
    unbilledFrom: string | null;
    /** Uninvoiced support work this client's prepaid block already paid for.
     *  `rollup.unbilled` is net of it — see ClientListRow#unbilled. */
    unbilledPrepaid: number;
    unbilledPrepaidHours: number;
  };
  /** Cost, revenue and blended margin across this client's whole portfolio. */
  rollup: PortfolioRollup;
  agreements: ClientAgreement[];
  invoices: ClientInvoice[];
  support: ClientSupport | null;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useClients(status: ClientStatusFilter, search: string) {
  return useQuery({
    queryKey: ['clients', status, search],
    queryFn: () =>
      api.get<{ clients: ClientListRow[] }>('/clients', {
        params: { status, search: search || undefined },
      }),
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'detail', id],
    queryFn: () => api.get<ClientDetail>(`/clients/${id}`),
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface ClientInput extends Partial<ClientBilling> {
  name: string;
  notes?: string | null;
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientInput) => api.post<{ id: string }>('/clients', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ClientInput> & { isActive?: boolean }) =>
      api.patch<{ id: string }>(`/clients/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

// ---------------------------------------------------------------------------
// Permanent deletion (archived clients, owner/admin only)
// ---------------------------------------------------------------------------

export interface ClientDeletionPreview {
  id: string;
  name: string;
  isActive: boolean;
  allowed: boolean;
  canDelete: boolean;
  reason: string | null;
  counts: {
    agreements: number;
    timeEntries: number;
    hours: number;
    invoices: number;
    payments: number;
    paymentsTotal: number;
    hourBlocks: number;
    recurringInvoices: number;
    expenses: number;
    forecastLinesKept: number;
  };
}

/** Fetched only for an ARCHIVED client — the server decides whether this
 *  viewer may delete it; the page never reads a role itself. */
export function useClientDeletion(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['clients', 'deletion', id],
    queryFn: () => api.get<ClientDeletionPreview>(`/clients/${id}/deletion`),
    enabled: Boolean(id) && enabled,
  });
}

export function useDeleteClient(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (confirmName: string) =>
      api.post<{ ok: true; name: string }>(`/clients/${id}/delete`, { confirmName }),
    // Everything under a client goes with it, so every money screen is stale.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
